import Dialog from '@mui/material/Dialog'

import type { DisciplineVerzameling } from '@models/DisciplineVerzameling'
import type { Entity } from '@models/Entity'
import type { GroupedFilterOption } from '@models/Filter'

import type { GeblokkeerdeWeek } from '../../../planning/types/GeblokkeerdeWeek'
import type { BijlageTemplate } from '../../../templates/types/BijlageTemplate'
import type { Subproces } from '../../subprocessen/types/Subproces'
import type { AssignedBijlage } from '../types/AssignedBijlage'
import type { Bijlage } from '../types/Bijlage'
import type { BijlagenDialogType } from '../types/BijlagenDialogType'

import { Cancel } from './dialogs/Cancel'
import { Create } from './dialogs/Create'
import { Edit } from './dialogs/Edit'
import { Opleveren } from './dialogs/Opleveren'
import { Review } from './dialogs/Review'
import { Upload } from './dialogs/Upload'

interface Props {
  type?: BijlagenDialogType
  bijlage?: Bijlage | AssignedBijlage
  assignees: GroupedFilterOption[]
  entity: Entity
  projectStartUitvoeringWeek?: string
  processtatus: string
  bijlageTemplates: BijlageTemplate[]
  disciplineVerzamelingen: DisciplineVerzameling[]
  isHoofdleidingProject?: boolean
  documentsoort?: string
  onClose: () => void
  geblokkeerdeWeken?: GeblokkeerdeWeek[]
  subproces?: Subproces
}

const isAssignedBijlage = (
  bijlage: Bijlage | AssignedBijlage,
): bijlage is AssignedBijlage =>
  bijlage && Object.keys(bijlage).includes('assignedTo')

export const BijlagenDialog = ({
  type,
  bijlage,
  assignees,
  onClose,
  entity,
  projectStartUitvoeringWeek,
  bijlageTemplates,
  disciplineVerzamelingen,
  processtatus,
  isHoofdleidingProject,
  documentsoort,
  geblokkeerdeWeken = [],
  subproces,
}: Props) => {
  if (!type) return null

  return (
    <Dialog open fullWidth maxWidth={'md'}>
      {type === 'submit' && bijlage && isAssignedBijlage(bijlage) && (
        <Opleveren
          bijlage={bijlage}
          assignees={assignees}
          entity={entity}
          onClose={onClose}
        />
      )}
      {type === 'create' && (
        <Create
          entity={entity}
          projectStartUitvoeringWeek={projectStartUitvoeringWeek}
          assignees={assignees}
          processtatus={processtatus}
          bijlageTemplates={bijlageTemplates}
          disciplineVerzamelingen={disciplineVerzamelingen}
          isHoofdleidingProject={isHoofdleidingProject}
          onClose={onClose}
          geblokkeerdeWeken={geblokkeerdeWeken}
          subproces={subproces}
        />
      )}
      {type === 'cancel' && bijlage && (
        <Cancel bijlage={bijlage} entity={entity} onClose={onClose} />
      )}
      {type === 'upload' && (
        <Upload
          bijlage={bijlage as AssignedBijlage | undefined}
          assignees={assignees}
          entity={entity}
          documentsoort={documentsoort}
          onClose={onClose}
          multiple
        />
      )}
      {type === 'review' && bijlage && isAssignedBijlage(bijlage) && (
        <Review bijlage={bijlage} entity={entity} onClose={onClose} />
      )}
      {type === 'edit' && bijlage && isAssignedBijlage(bijlage) && (
        <Edit
          bijlage={bijlage}
          entity={entity}
          onClose={onClose}
          geblokkeerdeWeken={geblokkeerdeWeken}
        />
      )}
    </Dialog>
  )
}
